// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\visites-particulieres/index.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Card, Table, Tag, Typography, Button, Row, Col, DatePicker, 
  Tooltip, Select, Space, Statistic 
} from "antd";
import { 
  StarFilled, ReloadOutlined, UserOutlined, 
  BarChartOutlined, WarningOutlined, CompassOutlined 
} from "@ant-design/icons";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as ChartTooltip, ResponsiveContainer, Legend 
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { getRapports } from "../../redux/slices/rapports/rapportsThunk";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import DoctorHistoryModal from "../../components/DoctorHistoryModal";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const VisitesParticulieresPage = () => {
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  
  // --- ÉTATS FILTRES ---
  const [filters, setFilters] = useState({
    search: "",
    delegateId: [],
    visitType: "ALL", // 'ALL' | 'NON_ABOUTIE' | 'SPECIFIQUE'
    dateInterval: [],
    specialOnly: "true",
  });

  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorHistory = (personId) => {
    if (!personId) return;
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };

  const rapportsState = useSelector((state) => state.rapports?.rapports || {});
  const rawData = rapportsState.data?.data || [];
  const total = rapportsState.data?.total || 0;
  const delegatesList = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);

  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  const fetchSpecialReports = useCallback(() => {
    const { dateInterval, visitType, ...apiFilters } = filters;
    const finalFilters = {
      ...apiFilters,
      specialOnly: "true",
      delegateId: filters.delegateId.length > 0 ? filters.delegateId : undefined,
      dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined,
      _t: Date.now()
    };
    dispatch(getRapports({ pageNumber: pagination.currentPage, limit: pagination.pageSize, filter: finalFilters }));
  }, [filters, pagination, dispatch]);

  useEffect(() => {
    fetchSpecialReports();
  }, [fetchSpecialReports]);

  // ⚡ Filtrage et classification des données
  const filteredData = useMemo(() => {
    return rawData.filter(record => {
      const isSpecific = 
        record.status === "VISITE_SPECIFIQUE" || 
        (record.note && record.note.includes("[VISITE SPÉCIFIQUE"));

      const isUnfinished = 
        record.status === "VISITE_NON_TERMINEE" || 
        record.status === "VISITE_NON_ABOUTIE" ||
        (record.note && record.note.includes("[VISITE NON"));

      if (!isSpecific && !isUnfinished) return false;

      // Filtre Type de visite
      if (filters.visitType === "NON_ABOUTIE" && !isUnfinished) return false;
      if (filters.visitType === "SPECIFIQUE" && !isSpecific) return false;

      return true;
    });
  }, [rawData, filters.visitType]);

  // ⚡ Calcul des statistiques pour le graphique par délégué
  const chartData = useMemo(() => {
    const statsMap = new Map();

    rawData.forEach(record => {
      const isSpecific = 
        record.status === "VISITE_SPECIFIQUE" || 
        (record.note && record.note.includes("[VISITE SPÉCIFIQUE"));

      const isUnfinished = 
        record.status === "VISITE_NON_TERMINEE" || 
        record.status === "VISITE_NON_ABOUTIE" ||
        (record.note && record.note.includes("[VISITE NON"));

      if (!isSpecific && !isUnfinished) return;

      const delName = record.delegate 
        ? `${record.delegate.firstname || ''} ${record.delegate.lastname || ''}`.trim().toUpperCase()
        : "INCONNU";

      if (!statsMap.has(delName)) {
        statsMap.set(delName, {
          delegate: delName,
          nonAbouties: 0,
          specifiques: 0,
          total: 0
        });
      }

      const entry = statsMap.get(delName);
      if (isUnfinished) entry.nonAbouties++;
      if (isSpecific) entry.specifiques++;
      entry.total++;
    });

    return Array.from(statsMap.values()).sort((a, b) => b.total - a.total);
  }, [rawData]);

  const totalNonAbouties = chartData.reduce((acc, curr) => acc + curr.nonAbouties, 0);
  const totalSpecifiques = chartData.reduce((acc, curr) => acc + curr.specifiques, 0);

  // ⚡ Nettoyage du texte des notes
  const formatMotifNote = (rawNote) => {
    if (!rawNote) return "-";
    let cleaned = rawNote
      .replace(/\[VISITE NON TERMINÉE\]/gi, "")
      .replace(/\[VISITE NON ABOUTIE\]/gi, "");

    cleaned = cleaned.replace(/\[VISITE SPÉCIFIQUE\s*-\s*([^\]]+)\]/gi, "$1 : ");
    cleaned = cleaned.replace(/\[VISITE SPÉCIFIQUE\]/gi, "");
    cleaned = cleaned.replace(/^[-:–\s]+/, "").trim();

    return cleaned || "-";
  };

  const columns = [
    {
      title: "Médecin / Client",
      dataIndex: "person",
      key: "person",
      width: 220,
      fixed: 'left',
      render: (person) => {
        if (!person) return 'N/A';
        const isPartner = person.isPartner;
        const fullName = person.type === 2 && person.pharmacien?.pharmacyName 
            ? `${person.pharmacien.pharmacyName} (${person.firstname} ${person.lastname})`
            : `Dr. ${person.firstname || ''} ${person.lastname || ''}`.trim();
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isPartner && (
              <Tooltip title={`PARTENAIRE : ${person.partnerNotes || ''}`}>
                <StarFilled style={{ color: '#fadb14', fontSize: '15px' }} />
              </Tooltip>
            )}
            
            <a 
              onClick={() => handleOpenDoctorHistory(person.id)} 
              style={{ 
                fontWeight: 'bold', 
                color: isPartner ? '#856404' : '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              {fullName}
            </a>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Type de Visite",
      key: "type_badge",
      width: 170,
      render: (_, record) => {
        const isSpecific = 
          record.status === "VISITE_SPECIFIQUE" || 
          (record.note && record.note.includes("[VISITE SPÉCIFIQUE"));

        if (isSpecific) {
          return <Tag color="blue" style={{ fontWeight: "bold" }}>🎯 Visite Spécifique</Tag>;
        }
        return <Tag color="volcano" style={{ fontWeight: "bold" }}>⚠️ Visite Non Aboutie</Tag>;
      },
    },
    {
      title: "Délégué",
      dataIndex: "delegate",
      key: "delegate",
      width: 160,
      render: (del) => del ? `${del.firstname} ${del.lastname}`.toUpperCase() : "N/A",
    },
    {
      title: "Région",
      dataIndex: "region",
      key: "region",
      width: 140,
      render: (reg) => reg?.name || "N/A",
    },
    {
      title: "Motif / Note",
      dataIndex: "note",
      key: "note",
      render: (note) => {
        const cleanText = formatMotifNote(note);
        return (
          <div style={{ maxWidth: 380 }}>
            <Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'voir plus' }} style={{ margin: 0 }}>
              {cleanText}
            </Typography.Paragraph>
          </div>
        );
      },
    },
    {
      title: "Échantillons Donnés",
      dataIndex: "reportSamples",
      key: "samples",
      width: 220,
      render: (samples) => {
        if (!samples || samples.length === 0) return <span style={{ color: '#aaa', fontStyle: 'italic' }}>Aucun</span>;
        return (
          <div>
            {samples.map((s, idx) => (
              <Tag color="cyan" key={idx} style={{ marginBottom: 2 }}>
                {s.sample?.name} (x{s.quantity}) {s.isSpecificRequest ? "⭐ Spécifique" : ""}
              </Tag>
            ))}
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      
      {/* --- EN-TÊTE --- */}
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>Suivi des Visites Non Abouties & Visites Spécifiques</Title>
        <Text type="secondary">Analyse graphique et historique détaillé des visites ciblées et des visites interrompues</Text>
      </div>

      {/* --- BLOC STATISTIQUES EN CARTOUCHES --- */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={12}>
          <Card bordered={false} style={{ borderRadius: 10, background: '#fffbe6', border: '1px solid #ffe58f' }}>
            <Statistic 
              title={<span style={{ color: '#d46b08', fontWeight: 'bold' }}>TOTAL VISITES NON ABOUTIES</span>}
              value={totalNonAbouties}
              valueStyle={{ color: '#fa541c', fontWeight: 'bold' }}
              prefix={<WarningOutlined />}
              suffix="visite(s)"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered={false} style={{ borderRadius: 10, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <Statistic 
              title={<span style={{ color: '#0369a1', fontWeight: 'bold' }}>TOTAL VISITES SPÉCIFIQUES</span>}
              value={totalSpecifiques}
              valueStyle={{ color: '#0288d1', fontWeight: 'bold' }}
              prefix={<CompassOutlined />}
              suffix="visite(s)"
            />
          </Card>
        </Col>
      </Row>

      {/* --- GRAPHIQUE COMPARATIF PAR DÉLÉGUÉ --- */}
      <Card 
        title={
          <Space>
            <BarChartOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: 'bold' }}>Comparatif des Visites Effectuées par Délégué</span>
          </Space>
        }
        bordered={false} 
        style={{ borderRadius: 12, marginBottom: 20 }}
      >
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="delegate" 
                interval={0} 
                angle={-25} 
                textAnchor="end" 
                height={60}
                tick={{ fontSize: 11, fontWeight: 'bold' }} 
              />
              <YAxis />
              <ChartTooltip />
              <Legend verticalAlign="top" align="right" />
              
              {/* Colonne Visites Non Abouties (Volcano / Orange) */}
              <Bar 
                name="Visites Non Abouties" 
                dataKey="nonAbouties" 
                fill="#fa541c" 
                radius={[4, 4, 0, 0]} 
              />

              {/* Colonne Visites Spécifiques (Bleu) */}
              <Bar 
                name="Visites Spécifiques" 
                dataKey="specifiques" 
                fill="#1890ff" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Text type="secondary">Aucune donnée à afficher sur le graphique pour cette sélection.</Text>
          </div>
        )}
      </Card>

      {/* --- TABLEAU ET FILTRES --- */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        
        {/* BARRE DE FILTRES : Délégués, Type de Visite, Période */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }} align="middle">
          
          {/* 1. FILTRE PAR DÉLÉGUÉ */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Filtrer par Délégué(s) :</Text>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Tous les délégués"
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={filters.delegateId}
              onChange={(val) => setFilters(prev => ({ ...prev, delegateId: val }))}
              options={delegatesList.map(d => ({
                label: `${d.firstname} ${d.lastname}`.toUpperCase(),
                value: d.id,
              }))}
            />
          </Col>

          {/* 2. FILTRE PAR TYPE DE VISITE */}
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Type de Visite :</Text>
            <Select
              style={{ width: '100%' }}
              value={filters.visitType}
              onChange={(val) => setFilters(prev => ({ ...prev, visitType: val }))}
            >
              <Option value="ALL">Tous les types</Option>
              <Option value="NON_ABOUTIE">⚠️ Visite Non Aboutie</Option>
              <Option value="SPECIFIQUE">🎯 Visite Spécifique</Option>
            </Select>
          </Col>

          {/* 3. FILTRE PAR PÉRIODE */}
          <Col xs={24} sm={12} md={7}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Période :</Text>
            <RangePicker 
              style={{ width: '100%' }}
              value={filters.dateInterval?.length === 2 ? [dayjs(filters.dateInterval[0]), dayjs(filters.dateInterval[1])] : null}
              onChange={(dates) => setFilters(prev => ({
                ...prev,
                dateInterval: dates ? [dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")] : []
              }))} 
            />
          </Col>

          {/* BOUTON RÉINITIALISER */}
          <Col xs={24} sm={12} md={3} style={{ textAlign: 'right', marginTop: 22 }}>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => setFilters({ search: "", delegateId: [], visitType: "ALL", dateInterval: [], specialOnly: "true" })}
            >
              Reset
            </Button>
          </Col>
        </Row>

        {/* TABLEAU DES VISITES */}
        <Table 
          columns={columns}
          dataSource={filteredData}
          loading={rapportsState.loading}
          rowKey="id"
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: total,
            onChange: (p, s) => setPagination({ currentPage: p, pageSize: s }),
            showTotal: (totalCount, range) => `${range[0]}-${range[1]} sur ${totalCount} visites`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* MODALE HISTORIQUE MÉDECIN */}
      <DoctorHistoryModal
        visible={doctorModalVisible}
        open={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
      />
    </div>
  );
};

export default VisitesParticulieresPage;